import * as echarts from 'echarts';
import { DefaultLabelFormatterCallbackParams } from 'echarts';
import {
  commonChartConfig,
  chartGridConfig,
  chartLegendConfig,
  getTooltipPositionFn,
} from '../common-chart-config';
import { getDataValuesAsArray } from './timelineUtils';
import {
  getLegendItemWidth,
  legendTooltipFormatter,
  LegendTooltipFormatterParams,
} from '../utils';
import { formatNumber, Locale } from '@ardoq/locale';
import {
  Aggregate,
  HexColor,
  Maybe,
  WidgetDataSourceTypes,
  WidgetTypes,
} from '@ardoq/api-types';
import { LineChartRenderData } from '../renderTypes';
import { LineChartWidgetProps } from './types';

type GetLineChartOptionsParam = {
  widgetRenderData: LineChartRenderData;
  dataPointColors: Record<string, HexColor>;
  userLocale: Locale;
};

export const getLineChartProps = ({
  widgetRenderData,
  dataPointColors,
  userLocale,
}: GetLineChartOptionsParam): LineChartWidgetProps & {
  viewType: WidgetTypes.LINE_CHART;
} => {
  return {
    ...widgetRenderData,
    isAbbreviated: widgetRenderData.isAbbreviated ?? false,
    hasDynamicYAxis: widgetRenderData.hasDynamicYAxis ?? false,
    dataPointColors,
    userLocale,
    color:
      widgetRenderData.datasource.sourceType === WidgetDataSourceTypes.METRICS
        ? ['#A002F1', '#0C6BC5']
        : commonChartConfig.color,
    width: widgetRenderData.layout.w,
  };
};

export const getLineChartOptions = ({
  data,
  color,
  dashboardContainerRef,
  userLocale,
  isAbbreviated,
  width,
  numberFormatOptions,
  aggregate,
  hasDynamicYAxis,
  dataPointColors,
}: LineChartWidgetProps & {
  dashboardContainerRef: Maybe<HTMLDivElement>;
}): echarts.EChartsOption => {
  const numberFormatOptionsForAggregate =
    aggregate !== Aggregate.COUNT ? numberFormatOptions : undefined;

  const dataValuesAsArray = getDataValuesAsArray(data, userLocale);

  const lineChartSeries = data.map((dataset, index) => {
    return {
      name: dataset.label ?? dataset.name,
      data: dataValuesAsArray[index].values,
      symbol: 'circle',
      symbolSize: 5,
      type: 'line',
      stack: null,
      areaStyle: null,
      silent: true,
      color: dataPointColors[dataset.name],
    };
  });

  // show legend for all charts with >1 line
  const legend =
    data.length > 1
      ? {
          ...chartLegendConfig,
          textStyle: {
            width: getLegendItemWidth(data.length, width),
            overflow: 'truncate',
          },
          tooltip: {
            formatter: (params: LegendTooltipFormatterParams) =>
              legendTooltipFormatter(params, width),
            show: true,
          },
        }
      : undefined;

  return {
    ...commonChartConfig,
    // TODO remove this after usage metrics BE will send color data
    color,
    tooltip: {
      trigger: 'axis',
      confine: false,
      extraCssText: 'max-width: 80vw',
      position: getTooltipPositionFn(dashboardContainerRef),
      formatter: data => `
      ${(data as DefaultLabelFormatterCallbackParams[])[0].name /* timestamp */}<br />
      ${(data as DefaultLabelFormatterCallbackParams[])
        .map(item => {
          const value = Array.isArray(item.value) ? item.value[1] : item.value;
          const formattedValue =
            typeof value === 'number'
              ? formatNumber(
                  value,
                  numberFormatOptionsForAggregate,
                  userLocale,
                  isAbbreviated
                )
              : String(value ?? '-');

          return `${item.marker}<b>${formattedValue}</b> ${item.seriesName}`;
        })
        .join('<br />')}
      `,
    },
    xAxis: {
      type: 'category',
      axisTick: {
        alignWithLabel: true,
      },
      axisLabel: {
        showMinLabel: true,
        showMaxLabel: true, // Forces the last label to always show
      },
    },
    yAxis: {
      type: 'value',
      boundaryGap: [0, '100%'],
      min: hasDynamicYAxis ? 'dataMin' : 0,
      max: 'dataMax',
      axisLabel: {
        formatter: value =>
          typeof value === 'number'
            ? formatNumber(
                value,
                numberFormatOptionsForAggregate,
                userLocale,
                isAbbreviated
              )
            : value,
      },
    },
    series: lineChartSeries as echarts.EChartsOption['series'],
    legend: legend as echarts.EChartsOption['legend'],
    grid: {
      ...chartGridConfig,
      top: '16px',
      right: '44px', // Needs some spacing on the right for the label of the last tick when displaying a date with a year
      ...(legend ? {} : { bottom: '16px' }),
    },
  };
};
